// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertPressure,
  convertTemperature,
  formatValueWithUnit,
} from '../../../../../../../../plugins/general';
import { isDakotaModel } from '../../../../../../../../plugins/crusher';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../../../components/EquipmentSensorList';
// Modules
import { ListModule } from '../../../ListModule';

// Component
import { Container, ListCol, ListContainer } from './Lubrication.style';
import { LubricationProps } from './Lubrication.types';

function Lubrication({
  className,
  unitData,
  testId,
}: LubricationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);
  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);

  // Check if the model is Dakota (extract from operationalData)
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);

  const returnTemp: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.lowAlarmDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempLowAlarm,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.lowWarningDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempLowWarning,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.crusherStartDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempCrusherStartPermissive,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.highAlarmDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempHighWarn,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.stopFeedDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempStopFeed,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.crusherStopDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempCrusherStop,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const oilPressure: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.normalPressureDescription')}`,
      setpoint: convertPressure(
        unitData?.spLubePressureNormal,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.feedStopDescription')}`,
      setpoint: convertPressure(
        unitData?.spLubePressureLowWarning,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.crusherStopLowAlarmDescription')}`,
      setpoint: convertPressure(
        unitData?.spLubePressureLowAlarm,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.lubeStopDescription')}`,
      setpoint: convertPressure(
        unitData?.spLubePressureStopAlarm,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const tankTemp: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.pumpRunDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeTankTempLowAlarm,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.pumpStartDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeTankTempLowWarning,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.highWarningDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeTankTempHighWarning,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.highAlarmDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeTankTempHighAlarm,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const lubeCooler: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.coolerStartDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeCoolerStartTemp,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.coolerStopDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeCoolerStopTemp,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const lubeHeater: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.startTempDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeHeaterStartTemp,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.stopTempDescription')}`,
      setpoint: convertTemperature(
        unitData?.spLubeHeaterStopTemp,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const supplyReturnDiff: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.lubrication.differentialAlarmDescription'),
      setpoint: convertTemperature(
        unitData?.spLubeSupplyReturnTempDiffAlarm,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.lubrication.delayAfterCoolerDescription'),
      setpoint: formatValueWithUnit(unitData?.spLubeSupplyReturnTempDiffDelay, 's'),
    },
  ];

  const supplyTemperature: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.lubrication.lowWarningDescription'),
      setpoint: convertTemperature(
        unitData?.spLubeSupplyTempLowWarn,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  // Lube Flow fields - only for Dakota models
  const lubeFlow: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.lubeFlowMinDescription')}`,
      setpoint: formatValueWithUnit(
        unitData?.spLubeFlowScaleMin,
        'GPM',
      ),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.lubrication.lubeFlowMaxDescription')}`,
      setpoint: formatValueWithUnit(
        unitData?.spLubeFlowScaleMax,
        'GPM',
      ),
    },
  ];

  return (
    <Container className={className} data-testid={testId}>
      <ListContainer>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.setPoint.lubrication.returnTempTitle')}
            items={returnTemp}
          />
        </ListCol>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.setPoint.lubrication.tankTempTitle')}
            items={tankTemp}
          />
        </ListCol>
        {/* Lube Pressure - only for non-Dakota models */}
        {!isDakota && (
          <ListCol xs={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.lubrication.lubePressureTitle')}
              items={oilPressure}
            />
          </ListCol>
        )}
        {/* Lube Flow - only for Dakota models with lubeFlowSwitchEnable */}
        {isDakota && unitData?.lubeFlowSwitchEnable && (
          <ListCol xs={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.lubrication.lubeFlowTitle')}
              items={lubeFlow}
            />
          </ListCol>
        )}
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.setPoint.lubrication.lubeCoolerTitle')}
            items={lubeCooler}
          />
        </ListCol>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.setPoint.lubrication.lubeHeaterTitle')}
            items={lubeHeater}
          />
        </ListCol>
        {unitData?.lubeSupplyTempEnable && (
          <ListCol xs={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.lubrication.supplyReturnTempTitle')}
              items={supplyReturnDiff}
            />
          </ListCol>
        )}
        {unitData?.lubeSupplyTempEnable && (
          <ListCol xs={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.lubrication.supplyTempTitle')}
              items={supplyTemperature}
            />
          </ListCol>
        )}
      </ListContainer>
    </Container>
  );
}

export { Lubrication };
